import { useMemo } from 'react'
import { useAdventureEditorStore } from '../stores/adventureEditorStore'
import { useAuthStore } from '@store/authStore'
import { useWorkflowStore } from '@store/workflowStore'
import { AdventureStatus, AdventureMainCategory } from '../types/adventure-editor.types'
import { FlowVersionStatus } from '../types/workflow.types'
import { UserRelationType } from '@api/types/session'

/**
 * Permission flags for published adventure editing restrictions.
 * When an adventure is published, most fields become read-only with specific exceptions.
 */
export interface PublishedPermissions {
  // State
  isPublished: boolean
  isOwner: boolean
  isModerator: boolean
  isSystemAdmin: boolean

  // Field permissions
  canEditTitle: boolean
  canEditQuickDescription: boolean
  canEditMainCategory: boolean
  canEditFullDescription: boolean
  canEditImages: boolean
  canEditTags: boolean
  canEditWorkflow: boolean
  canEditCriteria: boolean
  canEditTesters: boolean
  canEditEndDate: boolean
  canTogglePublic: boolean
  canEditLocation: boolean
  canEditStartDate: boolean
  canEditDifficulty: boolean
  canEditMaxParticipants: boolean
  canEditAllowGroups: boolean
  canEditRepeatable: boolean
  canEditMaintainers: boolean
  canEditModerators: boolean

  // Action permissions
  canSave: boolean
  canPublish: boolean
  canDelete: boolean
}

/**
 * Helper to check if adventure is published
 */
const checkIsPublished = (status: number | string | undefined): boolean => {
  if (status === undefined) return false
  const numStatus = typeof status === 'string' ? parseInt(status, 10) : status
  return numStatus === AdventureStatus.Published
}

/**
 * Helper to check if adventure is in testing phase
 */
const checkIsInTestingPhase = (status: number | string | undefined): boolean => {
  if (status === undefined) return false
  const numStatus = typeof status === 'string' ? parseInt(status, 10) : status
  return numStatus === AdventureStatus.Testing
}

/**
 * Helper to check if adventure is in draft
 */
const checkIsDraft = (status: number | string | undefined): boolean => {
  if (status === undefined) return true // New adventures are draft
  const numStatus = typeof status === 'string' ? parseInt(status, 10) : status
  return numStatus === AdventureStatus.Draft || isNaN(numStatus)
}

/**
 * Hook to get edit permissions based on published state and user roles.
 *
 * @param isMaintainer - Whether the current user is a maintainer on this adventure
 * @returns Permission flags for all editable fields and actions
 */
export function usePublishedPermissions(isMaintainer: boolean = false): PublishedPermissions {
  const user = useAuthStore((s) => s.user)
  const adventure = useAdventureEditorStore((s) => s.adventure)
  const generalInfo = useAdventureEditorStore((s) => s.generalInfo)
  const flowVersionStatus = useWorkflowStore((s) => s.versionStatus)
  const flowIsCompiled = useWorkflowStore((s) => s.isCompiled)

  return useMemo(() => {
    const status = adventure?.status
    const isPublished = checkIsPublished(status)
    const isDraft = checkIsDraft(status)
    const isInTestingPhase = checkIsInTestingPhase(status)

    // Check if this is a geocache or silent adventure type
    // These can be published directly from Draft without testing (no workflow to test)
    const isGeocache = generalInfo.mainCategory === AdventureMainCategory.TraditionalGeoCaches
    const isSilentAdventure = generalInfo.mainCategory === AdventureMainCategory.SilentAdventure
    const canPublishDirectly = isGeocache || isSilentAdventure

    // Check if flow is ready for publishing (in Testing or Published status)
    const flowIsInTestingOrPublished = flowVersionStatus === FlowVersionStatus.Testing ||
      flowVersionStatus === FlowVersionStatus.Published

    // Role checks
    const isOwner = !!(user?.userId && adventure?.creatorId && user.userId === adventure.creatorId)
    const isSystemAdmin = user?.roles?.includes('Admin') ?? false
    const isSystemModerator = isSystemAdmin || (user?.roles?.includes('Moderator') ?? false)

    // Check adventure-level roles via userRelationType bitmask
    const userRelation = adventure?.userRelationType ?? 0
    const isAdventureMaintainer = (userRelation & UserRelationType.Maintainer) !== 0
    const isAdventureModerator = (userRelation & UserRelationType.Moderator) !== 0

    // Combined moderator check (system OR adventure-level)
    const isModerator = isSystemModerator || isAdventureModerator

    // Check if user has management permission from the API
    // canManage is set by the backend based on ownership, maintainer status, or admin roles
    const canManage = adventure?.canManage ?? false

    // User can manage if: canManage from API, system admin/moderator, or adventure-level maintainer/moderator
    const hasManageAccess = canManage || isSystemAdmin || isSystemModerator || isAdventureMaintainer || isAdventureModerator

    // If user cannot manage this adventure, lock everything
    if (!hasManageAccess) {
      return {
        // State
        isPublished,
        isOwner,
        isModerator,
        isSystemAdmin,

        // No edit permissions for non-managers
        canEditTitle: false,
        canEditQuickDescription: false,
        canEditMainCategory: false,
        canEditFullDescription: false,
        canEditImages: false,
        canEditTags: false,
        canEditWorkflow: false,
        canEditCriteria: false,
        canEditTesters: false,
        canEditEndDate: false,
        canTogglePublic: false,
        canEditLocation: false,
        canEditStartDate: false,
        canEditDifficulty: false,
        canEditMaxParticipants: false,
        canEditAllowGroups: false,
        canEditRepeatable: false,
        canEditMaintainers: false,
        canEditModerators: false,

        // No actions for non-managers
        canSave: false,
        canPublish: false,
        canDelete: false,
      }
    }

    // When not published, everything is editable (for managers)
    if (!isPublished) {
      return {
        // State
        isPublished: false,
        isOwner,
        isModerator,
        isSystemAdmin,

        // All fields editable when not published
        canEditTitle: isDraft, // Title locked after draft
        canEditQuickDescription: true,
        canEditMainCategory: false, // Category is set at creation and cannot be changed
        canEditFullDescription: true,
        canEditImages: true,
        canEditTags: true,
        canEditWorkflow: true,
        canEditCriteria: true,
        canEditTesters: true,
        canEditEndDate: true,
        canTogglePublic: true,
        canEditLocation: true,
        canEditStartDate: true,
        canEditDifficulty: true,
        canEditMaxParticipants: true,
        canEditAllowGroups: true,
        canEditRepeatable: true,
        canEditMaintainers: true,
        canEditModerators: true,

        // Actions
        canSave: true,
        // Geocaches and silent adventures can publish directly from Draft
        // Other types require either adventure in testing phase OR flow in testing/published status
        canPublish: (isDraft && canPublishDirectly) || isInTestingPhase || flowIsInTestingOrPublished,
        canDelete: true,
      }
    }

    // Published adventure - apply restrictions (for managers)
    return {
      // State
      isPublished: true,
      isOwner,
      isModerator,
      isSystemAdmin,

      // Field permissions - most locked when published
      canEditTitle: false, // Always locked
      canEditQuickDescription: hasManageAccess, // Managers can edit quick description
      canEditMainCategory: false, // Locked for all
      canEditFullDescription: isModerator, // Admin/Mod only
      canEditImages: isModerator, // Admin/Mod only
      canEditTags: isModerator, // Admin/Mod only
      canEditWorkflow: false, // Always locked (export still works)
      canEditCriteria: isModerator, // Admin/Mod only
      canEditTesters: false, // Locked (testing is over)
      canEditEndDate: isOwner || isAdventureMaintainer || isMaintainer, // Owner + Maintainers
      canTogglePublic: generalInfo.isPublic === true, // Can only turn OFF (one-way)
      canEditLocation: false, // Locked for all
      canEditStartDate: false, // Locked for all
      canEditDifficulty: false, // Locked for all
      canEditMaxParticipants: false, // Locked for all
      canEditAllowGroups: hasManageAccess, // Only managers
      canEditRepeatable: hasManageAccess, // Only managers
      canEditMaintainers: hasManageAccess, // Only managers
      canEditModerators: hasManageAccess, // Only managers

      // Actions
      canSave: false, // Hidden when published
      canPublish: false, // Hidden when published
      canDelete: false, // Disabled when published
    }
  }, [user, adventure, generalInfo.isPublic, generalInfo.mainCategory, isMaintainer, flowVersionStatus, flowIsCompiled])
}
